import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { fleetKeys } from '@/hooks/fleet-keys';
import { useProvisioningPlaybooks } from '@/hooks/use-provisioning-playbooks';
import { useProvisioningTask } from '@/hooks/use-provisioning-task';
import { useRunPlaybook } from '@/hooks/use-run-playbook';
import { findActionPlaybook, type ServerActionDef } from '@/lib/server-actions';
import type { ServerDetail } from '@/types/fleet';
import { isTerminalStatus } from '@/types/provisioning';

/** A run that is currently being tracked for a given server. */
export interface ActiveRun {
  taskId: number;
  action: ServerActionDef;
  /** True for state-changing actions — drives the status-badge pending overlay. */
  showOverlay: boolean;
}

export interface UseFleetAction {
  /** The catalogue playbooks (so the menu can disable an unmapped action). */
  playbooksReady: boolean;
  playbooksError: boolean;
  /** Resolves an action to its playbook id, or undefined if not in the catalogue. */
  canRun: (action: ServerActionDef) => boolean;
  /** Triggers a run. `reason` is optional — none of the PR-E #2 actions require it. */
  run: (action: ServerActionDef, reason?: string) => void;
  /** True while a run POST is in flight. */
  isSubmitting: boolean;
  /** The task currently being tracked, if any. */
  activeRun: ActiveRun | null;
  /** Dismiss the task-status panel (does not cancel the task). */
  dismiss: () => void;
}

/**
 * Orchestrates the per-server action flow for one server:
 * POST run → toast with task id → track the task → on success invalidate the
 * server + summary queries, on failure surface an error toast. The returned
 * `activeRun` drives both the task-status side panel and the pending overlay.
 */
export function useFleetAction(server: ServerDetail): UseFleetAction {
  const queryClient = useQueryClient();
  const playbooksQuery = useProvisioningPlaybooks();
  const runMutation = useRunPlaybook();

  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);
  const settledRef = useRef<number | null>(null);

  const trackedTask = useProvisioningTask(activeRun?.taskId);

  const canRun = useCallback(
    (action: ServerActionDef): boolean =>
      Boolean(findActionPlaybook(action, playbooksQuery.data)),
    [playbooksQuery.data],
  );

  const run = useCallback(
    (action: ServerActionDef, reason?: string): void => {
      const playbook = findActionPlaybook(action, playbooksQuery.data);

      if (!playbook) {
        toast.error(`Playbook "${action.playbookName}" is not available.`);
        return;
      }

      runMutation.mutate(
        { playbookId: playbook.id, body: { args: action.buildArgs(server), reason } },
        {
          onSuccess: ({ task_id }) => {
            settledRef.current = null;
            setActiveRun({ taskId: task_id, action, showOverlay: action.stateChanging });
            toast.success(`${action.label} queued (task #${task_id}).`);
          },
          onError: (err) => {
            toast.error(`${action.label} failed to start: ${err.message}`);
          },
        },
      );
    },
    [playbooksQuery.data, runMutation, server],
  );

  // React to the tracked task settling — fire exactly once per task.
  useEffect(() => {
    const task = trackedTask.data;

    if (!task || !activeRun) return;
    if (!isTerminalStatus(task.status)) return;
    if (settledRef.current === task.task_id) return;

    settledRef.current = task.task_id;

    if (task.status === 'success') {
      toast.success(`${activeRun.action.label} succeeded (task #${task.task_id}).`);
      void queryClient.invalidateQueries({ queryKey: fleetKeys.server(server.name) });
      void queryClient.invalidateQueries({ queryKey: fleetKeys.summary() });
      // The list shows the status too; don't leave it stale until its next poll.
      void queryClient.invalidateQueries({ queryKey: fleetKeys.serverLists() });
      // The action completed — drop the pending overlay; keep the panel open.
      setActiveRun((prev) => (prev ? { ...prev, showOverlay: false } : prev));
    } else {
      toast.error(
        `${activeRun.action.label} ${task.status} (task #${task.task_id}). See the task panel for the log.`,
      );
      setActiveRun((prev) => (prev ? { ...prev, showOverlay: false } : prev));
    }
  }, [trackedTask.data, activeRun, queryClient, server.name]);

  const dismiss = useCallback(() => setActiveRun(null), []);

  return {
    playbooksReady: playbooksQuery.isSuccess,
    playbooksError: playbooksQuery.isError,
    canRun,
    run,
    isSubmitting: runMutation.isPending,
    activeRun,
    dismiss,
  };
}
